<!-- Backdrop -->
<div class="fixed inset-0 z-40 bg-black/40" (click)="closed.emit()" aria-hidden="true"></div>

<section
  role="dialog"
  aria-modal="true"
  aria-labelledby="bulk-details-title"
  class="fixed inset-y-0 right-0 z-50 w-full sm:max-w-lg bg-white shadow-2xl flex flex-col">
  <!-- Header -->
  <header class="flex items-start justify-between gap-3 p-4 border-b border-gray-200 bg-gray-50">
    <div class="min-w-0">
      <h2 id="bulk-details-title" class="text-lg font-bold text-gray-900 truncate">{{ request.organizationName }}</h2>
      <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <app-bulk-scrap-status-badge [status]="request.status" />
        <app-bulk-scrap-source-badge [source]="request.source" />
        <span>Received {{ request.createdAt | date: 'medium' }}</span>
      </div>
    </div>
    <button
      #closeButton
      type="button"
      (click)="closed.emit()"
      aria-label="Close bulk request details"
      class="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded-lg">
      <span aria-hidden="true" class="text-2xl leading-none">&times;</span>
    </button>
  </header>

  <div class="flex-1 overflow-y-auto p-4 space-y-5 text-sm">
    <!-- Contact -->
    <section aria-labelledby="bulk-contact-heading">
      <h3 id="bulk-contact-heading" class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Contact</h3>
      <div class="font-semibold text-gray-900">{{ request.contactPerson }}</div>
      <a [href]="'tel:' + request.contactPhone" class="inline-flex items-center min-h-[44px] text-emerald-700 font-semibold hover:underline">
        {{ request.contactPhone }}
      </a>
      <a *ngIf="request.contactEmail" [href]="'mailto:' + request.contactEmail" class="block text-gray-600 hover:underline break-all">
        {{ request.contactEmail }}
      </a>
    </section>

    <!-- Location -->
    <section aria-labelledby="bulk-location-heading">
      <h3 id="bulk-location-heading" class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Location</h3>
      <p class="text-gray-800">{{ request.address }}</p>
      <p *ngIf="request.locationName" class="text-gray-500">{{ request.locationName }}</p>
      <p *ngIf="request.pincode" class="text-gray-500">PIN: {{ request.pincode }}</p>
    </section>

    <!-- Scrap & pickup preference -->
    <section aria-labelledby="bulk-scrap-heading">
      <h3 id="bulk-scrap-heading" class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Scrap &amp; pickup</h3>
      <dl class="grid grid-cols-2 gap-x-4 gap-y-2">
        <dt class="text-gray-500">Estimated volume</dt>
        <dd class="font-semibold text-gray-900">{{ request.estimatedVolume }} {{ request.estimatedVolumeUnit }}</dd>
        <ng-container *ngIf="request.frequency">
          <dt class="text-gray-500">Frequency</dt>
          <dd class="text-gray-900">{{ request.frequency }}</dd>
        </ng-container>
        <dt class="text-gray-500">Preferred pickup</dt>
        <dd class="text-gray-900">
          <ng-container *ngIf="request.preferredPickupDate || request.preferredTimeSlot; else noPreference">
            {{ request.preferredPickupDate | date: 'mediumDate' }}
            <span *ngIf="request.preferredTimeSlot">· {{ request.preferredTimeSlot }}</span>
          </ng-container>
          <ng-template #noPreference><span class="text-gray-400 italic">No preference</span></ng-template>
        </dd>
      </dl>
      <ul class="mt-3 flex flex-wrap gap-1.5" aria-label="Scrap categories">
        <li *ngFor="let cat of request.categories" class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
          {{ cat.categoryName }}<span *ngIf="cat.estimatedQuantity" class="text-gray-500"> · {{ cat.estimatedQuantity }}</span>
        </li>
      </ul>
    </section>

    <!-- Customer notes -->
    <section aria-labelledby="bulk-notes-heading">
      <h3 id="bulk-notes-heading" class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Customer notes</h3>
      <p *ngIf="request.additionalNotes; else noNotes" class="whitespace-pre-line text-gray-800 bg-amber-50 border border-amber-100 rounded-lg p-3">{{ request.additionalNotes }}</p>
      <ng-template #noNotes><p class="text-gray-400 italic">No notes provided</p></ng-template>
    </section>

    <!-- Photos -->
    <section *ngIf="request.photoUrls?.length" aria-labelledby="bulk-photos-heading">
      <h3 id="bulk-photos-heading" class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Photos</h3>
      <div class="flex flex-wrap gap-2">
        <button
          *ngFor="let photo of request.photoUrls; let i = index"
          type="button"
          (click)="openPhoto(photo, $event)"
          [attr.aria-label]="'View photo ' + (i + 1)"
          class="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
          <img [src]="photo" alt="" class="w-20 h-20 object-cover rounded-lg border border-gray-200" />
        </button>
      </div>
    </section>

    <!-- Status -->
    <section aria-labelledby="bulk-status-heading">
      <div class="flex items-center justify-between mb-2">
        <h3 id="bulk-status-heading" class="text-xs font-bold text-gray-500 uppercase tracking-wider">Status history</h3>
        <button
          *ngIf="canManageStatus && !isEditingStatus"
          type="button"
          (click)="isEditingStatus = true"
          class="min-h-[44px] px-3 text-sm text-emerald-700 font-semibold hover:underline">
          Change status
        </button>
      </div>
      <app-bulk-scrap-status-editor
        *ngIf="isEditingStatus"
        class="block mb-4"
        [request]="request"
        (saved)="onStatusSaved($event)"
        (cancelled)="isEditingStatus = false" />
      <ol *ngIf="history.length; else noHistory" class="relative border-l border-gray-200 ml-2 space-y-4">
        <li *ngFor="let entry of history" class="ml-4">
          <span class="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-gray-300 border-2 border-white" aria-hidden="true"></span>
          <div class="flex flex-wrap items-center gap-2">
            <app-bulk-scrap-status-badge [status]="entry.status" />
            <time class="text-xs text-gray-500" [attr.datetime]="entry.updatedAt">{{ entry.updatedAt | date: 'medium' }}</time>
          </div>
          <p class="text-xs text-gray-500 mt-1">by {{ entry.updatedBy }}</p>
          <p *ngIf="entry.remark" class="text-gray-700 mt-1">{{ entry.remark }}</p>
        </li>
      </ol>
      <ng-template #noHistory><p class="text-gray-400 italic">No status changes yet</p></ng-template>
    </section>
  </div>
</section>
